/**
 * UploadTimeline is a bounded ring buffer of TimelineEntry records for a
 * single upload. It is the source of truth for what happened during an
 * upload's lifetime, feeding both the admin diagnostics drawer and the
 * DiagnosticSnapshot builder.
 *
 * Design notes:
 *   - Fixed capacity (default 200). When full, oldest entries drop first.
 *   - append() is O(1) via a rotating index — no array shift on hot path.
 *   - toArray() returns a defensive copy in chronological order (oldest→newest).
 *   - subscribe() lets the admin UI stream updates without polling.
 *   - No dependency on IndexedDB — persistence is a separate concern handled
 *     by TimelineStorage subscribing to this instance.
 */
export class UploadTimeline {
  /**
   * @param {string} uploadId
   * @param {{ capacity?: number }} [options]
   */
  constructor(uploadId, { capacity = 200 } = {}) {
    this.uploadId = uploadId;
    this._capacity = capacity;
    /** @type {Array<import('./UploadEvents').TimelineEntry | undefined>} */
    this._buffer = new Array(capacity);
    this._head = 0;      // next write index
    this._size = 0;      // count of live entries (≤ capacity)
    this._listeners = new Set();
  }

  /**
   * Append a new entry. Metadata is stored by reference — callers should not
   * mutate the object after passing it in.
   *
   * @param {string} event                 one of UPLOAD_EVENT.*
   * @param {Record<string, any>} [metadata]
   * @param {number} [timestamp]           defaults to Date.now()
   * @returns {import('./UploadEvents').TimelineEntry}
   */
  append(event, metadata = {}, timestamp = Date.now()) {
    const entry = { timestamp, event, metadata };
    this._buffer[this._head] = entry;
    this._head = (this._head + 1) % this._capacity;
    if (this._size < this._capacity) this._size += 1;
    this._notify(entry);
    return entry;
  }

  /**
   * @returns {Array<import('./UploadEvents').TimelineEntry>}
   *   Chronologically ordered copy (oldest first).
   */
  toArray() {
    if (this._size === 0) return [];
    const out = new Array(this._size);
    // If we've wrapped, oldest entry is at _head; otherwise it's at index 0.
    const start =
      this._size < this._capacity ? 0 : this._head;
    for (let i = 0; i < this._size; i += 1) {
      out[i] = this._buffer[(start + i) % this._capacity];
    }
    return out;
  }

  /** Number of live entries currently held. */
  size() {
    return this._size;
  }

  /** Total capacity (max entries retained). */
  capacity() {
    return this._capacity;
  }

  /** Most recent entry, or null. */
  latest() {
    if (this._size === 0) return null;
    const idx =
      (this._head - 1 + this._capacity) % this._capacity;
    return this._buffer[idx] || null;
  }

  /**
   * Return the last N entries matching an optional filter. Cheaper than
   * toArray().filter().slice(-n) because it walks backwards and stops early.
   *
   * @param {number} n
   * @param {(entry: import('./UploadEvents').TimelineEntry) => boolean} [predicate]
   */
  tail(n, predicate) {
    if (n <= 0 || this._size === 0) return [];
    const out = [];
    for (let i = 0; i < this._size && out.length < n; i += 1) {
      const idx =
        (this._head - 1 - i + this._capacity) % this._capacity;
      const entry = this._buffer[idx];
      if (!entry) continue;
      if (!predicate || predicate(entry)) out.push(entry);
    }
    return out.reverse();
  }

  /**
   * Subscribe to future appends. Called synchronously from append() so the
   * listener must not throw — throws are swallowed here to defend the writer.
   *
   * @param {(entry: import('./UploadEvents').TimelineEntry) => void} listener
   * @returns {() => void} unsubscribe
   */
  subscribe(listener) {
    if (typeof listener !== "function") return () => {};
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /** Drop all entries. Called on registry.dispose(uploadId). */
  clear() {
    this._buffer = new Array(this._capacity);
    this._head = 0;
    this._size = 0;
    // Deliberately do NOT clear listeners — the timeline instance may still
    // receive fresh entries later (e.g. tail retries after a "completed" event).
  }

  _notify(entry) {
    for (const listener of this._listeners) {
      try {
        listener(entry);
      } catch (_error) {
        // Never let observers break the writer.
      }
    }
  }
}
